import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { POSAdminLayout } from '@/features/pos/admin/components/POSAdminLayout';
import { useDiningAreas } from '@/features/pos/hooks/useDiningAreas';
import { useRestaurantTables, useCreateTable, useDeleteTable } from '@/features/pos/hooks/useRestaurantTables';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Loader2, Plus, Grid3x3, Trash2, MapPin } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from '@/components/ui/toast/useToast';
import { TableFormData } from '@/features/pos/types/admin';

export const Route = createFileRoute('/apps/pos/admin/tables/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: TablesPage,
});

function TablesPage() {
  const { data: areas = [] } = useDiningAreas();
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(
    areas.length > 0 ? areas[0].id : null
  );
  const { data: tables = [], isLoading } = useRestaurantTables(selectedAreaId || undefined);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const selectedArea = areas.find(a => a.id === selectedAreaId);

  if (areas.length === 0) {
    return (
      <POSAdminLayout>
        <div className="p-8">
          <div className="max-w-2xl mx-auto text-center py-12">
            <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2">No areas configured</h3>
            <p className="text-gray-600 mb-4">
              You need to create dining areas before adding tables
            </p>
            <Button onClick={() => window.location.href = '/apps/pos/admin/areas'}>
              <MapPin className="w-4 h-4 mr-2" />
              Configure Areas
            </Button>
          </div>
        </div>
      </POSAdminLayout>
    );
  }

  return (
    <POSAdminLayout>
      <div className="p-8">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Grid3x3 className="w-8 h-8 text-emerald-600" />
                <h1 className="text-3xl font-bold">Table Management</h1>
              </div>
              <p className="text-gray-600">Manage tables for each dining area</p>
            </div>
            <Button onClick={() => setCreateModalOpen(true)} size="lg" disabled={!selectedAreaId}>
              <Plus className="w-4 h-4 mr-2" />
              Add Table
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Area Selector */}
            <div className="lg:col-span-1">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Dining Areas</h3>
              <div className="space-y-2">
                {areas.map((area) => (
                  <button
                    key={area.id}
                    onClick={() => setSelectedAreaId(area.id)}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      selectedAreaId === area.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: area.color + '20' }}
                      >
                        <MapPin className="w-4 h-4" style={{ color: area.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{area.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{area.area_type}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tables List */}
            <div className="lg:col-span-3">
              {selectedArea && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      {selectedArea.name} - Tables ({tables.length})
                    </h3>
                  </div>

                  {isLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                    </div>
                  ) : tables.length === 0 ? (
                    <Card className="p-12 text-center">
                      <Grid3x3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-semibold mb-2">No tables yet</h3>
                      <p className="text-gray-500 mb-4">Add tables to this area</p>
                      <Button onClick={() => setCreateModalOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Table
                      </Button>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {tables.map((table) => (
                        <TableCard key={table.id} table={table} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Table Modal */}
      {selectedAreaId && (
        <CreateTableModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          areaId={selectedAreaId}
          existingTables={tables}
        />
      )}
    </POSAdminLayout>
  );
}

function TableCard({ table }: { table: any }) {
  const deleteTable = useDeleteTable();

  const handleDelete = async () => {
    if (confirm(`Delete table ${table.table_number}?`)) {
      try {
        await deleteTable.mutateAsync(table.id);
        toast.success('Deleted', 'Table deleted');
      } catch (error: any) {
        toast.error('Error', error.message);
      }
    }
  };

  return (
    <Card className="p-4 relative group">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDelete}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:bg-red-50"
      >
        <Trash2 className="w-4 h-4" />
      </Button>

      <div className="text-center">
        <Grid3x3 className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
        <p className="font-bold text-lg">{table.table_number}</p>
        {table.table_name && (
          <p className="text-sm text-gray-600">{table.table_name}</p>
        )}
        <p className="text-xs text-gray-500 mt-2">{table.seats} seats</p>
        <div className="mt-2">
          <span className={`text-xs px-2 py-1 rounded-full ${
            table.status === 'available' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}>
            {table.status}
          </span>
        </div>
      </div>
    </Card>
  );
}

function CreateTableModal({ 
  open, 
  onClose, 
  areaId,
  existingTables 
}: { 
  open: boolean; 
  onClose: () => void; 
  areaId: string;
  existingTables: any[];
}) {
  const createTable = useCreateTable();
  const { register, handleSubmit, setValue, watch, reset } = useForm<TableFormData>({
    defaultValues: {
      table_number: '',
      table_name: '',
      seats: 4,
      shape: 'square',
    },
  });

  // Auto-generate next table number
  const generateNextNumber = () => {
    const numbers = existingTables
      .map(t => parseInt(t.table_number.replace(/\D/g, '')))
      .filter(n => !isNaN(n));
    
    const nextNum = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    return `T-${String(nextNum).padStart(2, '0')}`;
  };

  const onSubmit = async (data: TableFormData) => {
    try {
      await createTable.mutateAsync({
        ...data,
        area_id: areaId,
      });
      toast.success('Created', 'Table created successfully');
      reset();
      onClose();
    } catch (error: any) {
      toast.error('Error', error.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Table</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>Table Number *</Label>
            <div className="flex gap-2">
              <Input {...register('table_number', { required: true })} placeholder="T-01" />
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setValue('table_number', generateNextNumber())}
              >
                Auto
              </Button>
            </div>
          </div>
          <div>
            <Label>Table Name (Optional)</Label>
            <Input {...register('table_name')} placeholder="Window table" />
          </div>
          <div>
            <Label>Number of Seats</Label>
            <Input type="number" {...register('seats', { valueAsNumber: true, required: true })} />
          </div>
          <div>
            <Label>Shape</Label>
            <Select value={watch('shape')} onValueChange={(v: any) => setValue('shape', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="square">Square</SelectItem>
                <SelectItem value="rectangle">Rectangle</SelectItem>
                <SelectItem value="circle">Circle</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createTable.isPending}>
              {createTable.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              ) : (
                <>Create Table</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
